import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import PasswordRulesChecklist, { isPasswordStrong } from "./PasswordRulesChecklist";

/**
 * Modale forçant le changement de mot de passe après une réinitialisation admin.
 * S'affiche tant que `user.must_change_password === true` ; non-fermable.
 */
export default function ForcePasswordChangeDialog() {
  const { user, setUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const open = Boolean(user && user.must_change_password);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(newPassword)) {
      toast.error("Le mot de passe ne respecte pas la politique de sécurité");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Mot de passe mis à jour");
      setUser({ ...user, must_change_password: false });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="force-password-change-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} className="text-blue-600" />
            Définissez votre nouveau mot de passe
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600 leading-relaxed">
          Votre mot de passe a été réinitialisé par votre administrateur.
          Pour des raisons de sécurité, vous devez en choisir un nouveau avant
          de continuer.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="force-password-change-form">
          <div>
            <Label htmlFor="cur-pwd" className="label-uppercase block mb-2">
              Mot de passe temporaire reçu
            </Label>
            <div className="relative">
              <Input
                id="cur-pwd"
                type={showCurrent ? "text" : "password"}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="h-11 pr-11"
                data-testid="force-current-password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                title={showCurrent ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
                data-testid="force-current-password-toggle"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new-pwd" className="label-uppercase block mb-2">
              Nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                id="new-pwd"
                type={showNew ? "text" : "password"}
                required
                minLength={12}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-11 pr-11"
                data-testid="force-new-password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                title={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
                data-testid="force-new-password-toggle"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="mt-2 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
              <p className="label-uppercase mb-1.5 text-zinc-600">Politique de sécurité</p>
              <PasswordRulesChecklist password={newPassword} />
            </div>
          </div>
          <div>
            <Label htmlFor="cfm-pwd" className="label-uppercase block mb-2">
              Confirmation du nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                id="cfm-pwd"
                type={showConfirm ? "text" : "password"}
                required
                minLength={12}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-11 pr-11"
                data-testid="force-confirm-password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                title={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
                data-testid="force-confirm-password-toggle"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={logout}
              className="text-xs text-zinc-500 hover:text-zinc-800 underline-offset-2 hover:underline"
              data-testid="force-pwd-logout"
            >
              Se déconnecter
            </button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="force-pwd-submit"
            >
              {loading ? "Enregistrement…" : "Valider"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
